<template>
    <div>
        <vue-headful :title="'Subcontractor Settings :: MeritEHS'"/>

        <h4 class="py-3 mb-4">
            <ol class="breadcrumb font-weight-bold m-0">
                <li class="breadcrumb-item font-weight-light">
                    <router-link :to="`/subcontractors`">
                        <i class="fa fa-id-badge"></i> Subcontractors
                    </router-link>
                </li>
                <li class="breadcrumb-item active">Settings</li>
            </ol>
        </h4>

        <ul class="search-nav nav nav-tabs tabs-alt container-p-x container-m--x mb-4 d-lg-flex d-none">
            <template v-for="(tab, key) in settingsTabs">
                <b-nav-item :active="curTab === key" @click="curTab = key">
                    {{ tab }}
                </b-nav-item>
            </template>
        </ul>

        <div class="mb-2 mb-lg-0 d-lg-none d-block">
          <b-dropdown :html="tabButtonTitle" variant="default" class="bg-white">
            <template v-for="(tab, key) in settingsTabs">
                <b-dropdown-item :active="curTab === key" @click="curTab = key">
                    {{ tab }}
                </b-dropdown-item>
            </template>
          </b-dropdown>
        </div>

        <div class="row">
            <div class="col-xl-9 col-lg-12">

                <div v-if="curTab === 'general'">
                    <div v-if="loading" class="text-center">
                        <i class="fa fa-circle-o-notch fa-spin"></i> Loading
                    </div>
                    <div v-else>

                        <div class="card mb-4">
                            <div class="card-header">
                                <h4 class="mb-0">General Settings</h4>
                            </div>
                            <div class="card-body">
                                <div class="form-group row">
                                    <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.sub_expire_date }">
                                        Sub Expiration Date:
                                    </label>
                                    <div class="col-sm-8">
                                        <b-form-input type="text" placeholder="02-20" v-model="account.sub_expire_date" :class="{'is-invalid': errors.sub_expire_date}"></b-form-input>
                                        <small class="text-muted">The date when the current year of data becomes required for prequalification</small>
                                        <span v-if="errors.sub_expire_date" class="text-danger">
                                            <br>
                                            <span v-for="error in errors.sub_expire_date">{{ error }}</span>
                                        </span>
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.sub_years_required }">
                                        Sub Years Required:<br>
                                    </label>
                                    <div class="col-sm-8">
                                        <b-form-input type="number" placeholder="Number of Years" v-model="account.sub_years_required" :class="{'is-invalid': errors.sub_years_required}"></b-form-input>
                                        <small class="text-muted">The number of years of data required for the prequalification form</small>
                                        <span v-if="errors.sub_years_required" class="text-danger">
                                            <br>
                                            <span v-for="error in errors.sub_years_required">{{ error }}</span>
                                        </span>
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.sub_contact_email }">
                                        Sub Contact Email:<br>
                                    </label>
                                    <div class="col-sm-8">
                                        <b-form-input type="email" placeholder="Contact Email" v-model="account.sub_contact_email" :class="{'is-invalid': errors.sub_contact_email}"></b-form-input>
                                        <small class="text-muted">The email a subcontractor can email to submit extra data or ask questions. This will be displayed on the Pre-Qualification form for Subcontractors to see.</small>
                                        <span v-if="errors.sub_contact_email" class="text-danger">
                                            <br>
                                            <span v-for="error in errors.sub_contact_email">{{ error }}</span>
                                        </span>
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.sub_expiring_message }">
                                        Sub Expiration Message:<br>
                                    </label>
                                    <div class="col-sm-8">
                                        <b-form-textarea placeholder="Enter Expiration Message" v-model="account.sub_expiring_message" :class="{'is-invalid': errors.sub_expiring_message}" rows="5"></b-form-textarea>

                                        <small class="text-muted">This is the message that will be emailed to subcontractors 60 and 30 days before their approval expires.</small><br/>
                                        <small class="text-muted">NOTE: Use "[days]" as a placeholder for the number of days until expiration. MeritEHS will replace this with the number of days. Example: "Your approval with Company Name is expiring in [days] days. Please make sure to update your information."</small>

                                        <span v-if="errors.sub_expiring_message" class="text-danger">
                                            <br>
                                            <span v-for="error in errors.sub_expiring_message">{{ error }}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="card mb-4">
                            <div class="card-header">
                                <h4 class="mb-0">
                                    Subcontractor Rating Evaluation Form
                                </h4>
                            </div>
                            <div class="card-body">
                                <div class="mb-4 alert alert-info">
                                    <strong>What is the Subcontractor Rating Evaluation Form?</strong> This evaluation form will allows subcontractor views to fill out an evaluation on the sub's performance. Each question contributes to their overall score, along with a "Would you recommend them? Yes/No" and optional comments. Each question has a minimum value of 1.
                                </div>
                                <div class="form-group row">
                                    <b-form-group class="col-6" label="Rating Legend">
                                        <b-form-textarea rows="3" v-model="account.sub_rating_legend" placeholder="Enter Legend or Instructions, such as 1 - Poor, 2 - Fair, 3 - Good, etc." />
                                    </b-form-group>
                                </div>
                                <div class="form-group row">
                                    <label class="col-12 col-form-label">Rating Questions ({{ account.sub_rating_questions.length }}):</label>
                                    <div class="col-12 mt-2">
                                        <div v-for="(question,index) in account.sub_rating_questions">
                                            <b-form-group class="form-inline mb-1">
                                                <b-button variant="danger" size="xs" @click="deleteQuestion(index)" title="Delete Question"><i class="fa fa-trash"></i></b-button>
                                                <strong class="ml-1 mr-1" :class="{'text-danger': errors['sub_rating_questions.'+index+'.max_rating']}">Maximum Rating:</strong>
                                                <b-form-input placeholder="Maximum Rating" type="number" min="1" v-model="question.max_rating" style="width: 75px;" :class="{'is-invalid': errors['sub_rating_questions.'+index+'.max_rating']}" />
                                                <span v-if="errors['sub_rating_questions.'+index+'.max_rating']" class="text-danger">{{ errors['sub_rating_questions.'+index+'.max_rating'][0] }}</span>
                                            </b-form-group>
                                            <b-form-group>
                                                <div class="mb-1">
                                                    <strong class="mr-1" :class="{'text-danger': errors['sub_rating_questions.'+index+'.question']}">Question</strong>
                                                </div>
                                                <b-form-textarea placeholder="Enter Rating Question Details" v-model="question.question" :class="{'is-invalid': errors['sub_rating_questions.'+index+'.question']}" />
                                                <div v-if="errors['sub_rating_questions.'+index+'.question']" class="text-danger">{{ errors['sub_rating_questions.'+index+'.question'][0] }}</div>
                                            </b-form-group>
                                            <hr>
                                        </div>
                                        <button class="btn btn-sm btn-info" @click="newQuestion"><i class="fa fa-plus"></i> Add Question</button>
                                    </div>
                                </div>
                            </div>
                            <div class="card-footer">
                                <button type="button" class="btn btn-success" @click="saveSettings()" :disabled="saving">
                                    Update Settings
                                </button>
                            </div>
                        </div>

                    </div>
                </div>

                <div v-else-if="curTab === 'trades'">
                    <div class="card mb-4">
                        <div class="card-header">
                            <h4 class="mb-0">Trades List
                                <router-link :to="`/subcontractors/settings/trades/create`" class="btn btn-sm btn-success"><i class="fa fa-plus"></i> New</router-link>
                            </h4>
                        </div>
                        <div class="card-body">
                            <div v-if="loading" class="text-center">
                                <i class="fa fa-circle-o-notch fa-spin"></i> Loading
                            </div>
                            <div v-else>
                                <div class="form-group">
                                    <trade-list />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else-if="curTab === 'field_trainings'">
                    <div class="card mb-4">
                        <div class="card-header">
                            <h4 class="mb-0">Field Trainings List
                                <router-link :to="`/subcontractors/settings/field-trainings/create`" class="btn btn-sm btn-success"><i class="fa fa-plus"></i> New</router-link>
                            </h4>
                        </div>
                        <div class="card-body">
                            <div v-if="loading" class="text-center">
                                <i class="fa fa-circle-o-notch fa-spin"></i> Loading
                            </div>
                            <div v-else>
                                <div class="form-group">
                                    <field-training-list />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else-if="curTab === 'prequalification'">
                    <div class="card" style="margin-bottom: 50px">
                        <div class="card-header">
                            <strong>Section 1: General Information</strong>

                            <div class="float-right">
                                <button class="btn btn-sm btn-light" @click="addField(1)" title="Add Custom Field">
                                    <i class="fa fa-plus-circle text-success"></i> Custom Field
                                </button>
                            </div>
                        </div>
                        <div class="card-body">
                            <div v-if="loading" class="text-center">
                                <i class="fa fa-circle-o-notch fa-spin"></i> Loading
                            </div>
                            <div v-else class="form-horizontal">
                                <div class="form-group row">
                                    <label class="col-md-4 text-md-right control-label text-muted">Pre-Qualified through a 3rd Party (Yes/No):</label>
                                    <div class="col-md-5">
                                        <b-form-select v-model="sub_settings[1].pre_qual_3rd_question_show_hide" size="sm" :options="[{text: 'Show', value: 1}, {text: 'Hide', value: 0}]" />

                                        <b-form-select
                                            v-model="sub_settings[1].pre_qual_3rd_question_req"
                                            size="sm"
                                            class="mt-1"
                                            :options="[{text: 'Required', value: 1}, {text: 'Optional', value: 0}]"
                                            v-if="sub_settings[1].pre_qual_3rd_question_show_hide === 1" />
                                    </div>
                                </div>

                                <div class="form-group row">
                                    <label class="col-md-4 text-md-right control-label text-muted">Pre-Qualified 3rd Party Certification File:</label>
                                    <div class="col-md-5">
                                        <b-form-select v-model="sub_settings[1].pre_qual_3rd_file_show_hide" size="sm" :options="[{text: 'Show', value: 1}, {text: 'Hide', value: 0}]" />

                                        <b-form-select
                                            v-model="sub_settings[1].pre_qual_3rd_file_req"
                                            size="sm"
                                            class="mt-1"
                                            :options="[{text: 'Required', value: 1}, {text: 'Optional', value: 0}]"
                                            v-if="sub_settings[1].pre_qual_3rd_file_show_hide === 1" />
                                    </div>
                                </div>

                                <hr v-if="custom_field_sections[1] && custom_field_sections[1].fields.length > 0" />

                                <custom-field-edit
                                    ref="customFieldEdit_Section_1"
                                    :sectionId="1"
                                    :enableAddToList="true"
                                    :startingFields="(custom_field_sections[1] && custom_field_sections[1].fields ? custom_field_sections[1].fields : [])"
                                    @fieldsChanged="((fields) => { this.custom_field_sections[1].fields = fields; })" />
                            </div>
                        </div>
                    </div>

                    <div class="card" style="margin-bottom: 50px">
                        <div class="card-header">
                            <strong>Section 2: Company Information</strong>

                            <div class="float-right">
                                <button class="btn btn-sm btn-light" @click="addField(2)" title="Add Custom Field">
                                    <i class="fa fa-plus-circle text-success"></i> Custom Field
                                </button>
                            </div>
                        </div>
                        <div class="card-body">
                            <div v-if="loading" class="text-center">
                                <i class="fa fa-circle-o-notch fa-spin"></i> Loading
                            </div>
                            <div v-else>
                                <div class="mb-2">
                                    Designation Options
                                    <router-link :to="`/subcontractors/settings/designations/create`" class="btn btn-xs btn-light" title="Add Designation Option"><i class="fa fa-plus-circle text-success"></i> Add</router-link>
                                </div>
                                <designation-list />
                                <hr />

                                <div class="mb-2">
                                    License Options
                                    <router-link :to="`/subcontractors/settings/licenses/create`" class="btn btn-xs btn-light" title="Add License Option"><i class="fa fa-plus-circle text-success"></i> Add</router-link>
                                </div>
                                <license-list />
                                <hr v-if="custom_field_sections[2] && custom_field_sections[2].fields.length > 0" />

                                <custom-field-edit
                                    ref="customFieldEdit_Section_2"
                                    :sectionId="2"
                                    :enableAddToList="true"
                                    :startingFields="(custom_field_sections[2] && custom_field_sections[2].fields ? custom_field_sections[2].fields : [])"
                                    @fieldsChanged="((fields) => { this.custom_field_sections[2].fields = fields; })" />
                            </div>
                        </div>
                    </div>

                    <div class="card" style="margin-bottom: 50px">
                        <div class="card-header">
                            <strong>Section 3: Work Performed</strong>

                            <div class="float-right">
                                <button class="btn btn-sm btn-light" @click="addField(3)" title="Add Custom Field">
                                    <i class="fa fa-plus-circle text-success"></i> Custom Field
                                </button>
                            </div>
                        </div>
                        <div class="card-body">
                            <div v-if="loading" class="text-center">
                                <i class="fa fa-circle-o-notch fa-spin"></i> Loading
                            </div>
                            <div v-else>
                                <div class="form-group row">
                                    <label class="col-md-4 text-md-right control-label text-muted">Areas of Work:</label>
                                    <div class="col-md-5">
                                        <b-form-select v-model="sub_settings[3].areas_of_work_show_hide" size="sm" :options="[{text: 'Show', value: 1}, {text: 'Hide', value: 0}]" />

                                        <b-form-select v-model="sub_settings[3].areas_of_work_req" size="sm" class="mt-1" :options="[{text: 'Required', value: 1}, {text: 'Optional', value: 0}]" v-if="sub_settings[3].areas_of_work_show_hide === 1" />
                                    </div>
                                </div>

                                <template v-if="sub_settings[3].areas_of_work_show_hide == 1">
                                    <div class="mb-2">
                                        Areas of Work
                                        <router-link :to="`/subcontractors/settings/areas-of-work/edit`" class="btn btn-xs btn-light" title="Edit Areas of Work Option"><i class="fa fa-edit text-success"></i> Edit</router-link>
                                    </div>
                                    <work-area-list />
                                </template>

                                <hr />
                                <div class="mb-2">
                                    Business Registrations
                                    <router-link :to="`/subcontractors/settings/registrations/edit`" class="btn btn-xs btn-light" title="Edit Business Registrations Options"><i class="fa fa-edit text-success"></i> Edit</router-link>
                                </div>
                                <registration-list />
                                <hr v-if="custom_field_sections[3] && custom_field_sections[3].fields.length > 0" />

                                <custom-field-edit
                                    ref="customFieldEdit_Section_3"
                                    :sectionId="3"
                                    :enableAddToList="true"
                                    :startingFields="(custom_field_sections[3] && custom_field_sections[3].fields ? custom_field_sections[3].fields : [])"
                                    @fieldsChanged="((fields) => { this.custom_field_sections[3].fields = fields; })" />
                            </div>
                        </div>
                    </div>

                    <div class="card" style="margin-bottom: 50px">
                        <div class="card-header">
                            <strong>Section 4: Financial Information</strong>

                            <div class="float-right">
                                <button class="btn btn-sm btn-light" @click="addField(4)" title="Add Custom Field">
                                    <i class="fa fa-plus-circle text-success"></i> Custom Field
                                </button>
                            </div>
                        </div>
                        <div class="card-body">
                            <div v-if="loading" class="text-center">
                                <i class="fa fa-circle-o-notch fa-spin"></i> Loading
                            </div>
                            <div v-else>
                                <div>
                                    -- No Settings --
                                </div>
                                <hr/>

                                <custom-field-edit
                                    ref="customFieldEdit_Section_4"
                                    :sectionId="4"
                                    :enableAddToList="true"
                                    :startingFields="(custom_field_sections[4] && custom_field_sections[4].fields ? custom_field_sections[4].fields : [])"
                                    @fieldsChanged="((fields) => { this.custom_field_sections[4].fields = fields; })" />
                            </div>
                        </div>
                    </div>
                    <div class="card" style="margin-bottom: 50px">
                        <div class="card-header">
                            <strong>Section 5: Health, Safety & Environmental</strong>

                            <div class="float-right">
                                <button class="btn btn-sm btn-light" @click="addField(5)" title="Add Custom Field">
                                    <i class="fa fa-plus-circle text-success"></i> Custom Field
                                </button>
                            </div>
                        </div>
                        <div class="card-body">
                            <div v-if="loading" class="text-center">
                                <i class="fa fa-circle-o-notch fa-spin"></i> Loading
                            </div>
                            <div v-else class="form-horizontal">
                                <div class="form-group row">
                                    <label class="col-md-4 text-md-right control-label text-muted">HSE Program File:</label>
                                    <div class="col-md-5">
                                        <b-form-select v-model="sub_settings[5].hse_prog_file_show_hide" size="sm" :options="[{text: 'Show', value: 1}, {text: 'Hide', value: 0}]" />

                                        <b-form-select v-model="sub_settings[5].hse_prog_file_req" size="sm" class="mt-1" :options="[{text: 'Required', value: 1}, {text: 'Optional', value: 0}]" v-if="sub_settings[5].hse_prog_file_show_hide === 1" />
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <label class="col-md-4 text-md-right control-label text-muted">Field Craft Personnel Training:</label>
                                    <div class="col-md-5">
                                        <b-form-select v-model="sub_settings[5].field_craft_pers_show_hide" size="sm" :options="[{text: 'Show', value: 1}, {text: 'Hide', value: 0}]" />
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <label class="col-md-4 text-md-right control-label text-muted">List any Safety Differentiators:</label>
                                    <div class="col-md-5">
                                        <b-form-select v-model="sub_settings[5].safety_diff_show_hide" size="sm" :options="[{text: 'Show', value: 1}, {text: 'Hide', value: 0}]" />
                                    </div>
                                </div>

                                <hr v-if="custom_field_sections[5] && custom_field_sections[5].fields.length > 0" />

                                <custom-field-edit
                                    ref="customFieldEdit_Section_5"
                                    :sectionId="5"
                                    :enableAddToList="true"
                                    :startingFields="(custom_field_sections[5] && custom_field_sections[5].fields ? custom_field_sections[5].fields : [])"
                                    @fieldsChanged="((fields) => { this.custom_field_sections[5].fields = fields; })" />
                            </div>
                        </div>
                    </div>
                    <div class="card" style="margin-bottom: 50px">
                        <div class="card-header">
                            <strong>Section 6: Insurance Information</strong>

                            <div class="float-right">
                                <button class="btn btn-sm btn-light" @click="addField(6)" title="Add Custom Field">
                                    <i class="fa fa-plus-circle text-success"></i> Custom Field
                                </button>
                            </div>
                        </div>
                        <div class="card-body">
                            <div v-if="loading" class="text-center">
                                <i class="fa fa-circle-o-notch fa-spin"></i> Loading
                            </div>
                            <div v-else>
                                <div>
                                    -- No Settings --
                                </div>
                                <hr/>

                                <custom-field-edit
                                    ref="customFieldEdit_Section_6"
                                    :sectionId="6"
                                    :enableAddToList="true"
                                    :startingFields="(custom_field_sections[6] && custom_field_sections[6].fields ? custom_field_sections[6].fields : [])"
                                    @fieldsChanged="((fields) => { this.custom_field_sections[6].fields = fields; })" />
                            </div>
                        </div>
                    </div>
                    <div class="card" style="margin-bottom: 50px">
                        <div class="card-header">
                            <strong>Section 7: Bonding Information</strong>

                            <div class="float-right">
                                <button class="btn btn-sm btn-light" @click="addField(7)" title="Add Custom Field">
                                    <i class="fa fa-plus-circle text-success"></i> Custom Field
                                </button>
                            </div>
                        </div>
                        <div class="card-body">
                            <div v-if="loading" class="text-center">
                                <i class="fa fa-circle-o-notch fa-spin"></i> Loading
                            </div>
                            <div v-else class="form-horizontal">
                                <div class="form-group row">
                                    <label class="col-md-4 text-md-right control-label text-muted">Bonding Company:</label>
                                    <div class="col-md-5">
                                        <b-form-select
                                            v-model="sub_settings[7].bonding_company_req"
                                            size="sm"
                                            class="mt-1"
                                            :options="[{text: 'Required', value: 1}, {text: 'Optional', value: 0}]" />
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <label class="col-md-4 text-md-right control-label text-muted">Copy of Bonding Letter::</label>
                                    <div class="col-md-5">
                                        <b-form-select
                                            v-model="sub_settings[7].bonding_file_id_req"
                                            size="sm"
                                            class="mt-1"
                                            :options="[{text: 'Required', value: 1}, {text: 'Optional', value: 0}]" />
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <label class="col-md-4 text-md-right control-label text-muted">Bonding Agent & Firm:</label>
                                    <div class="col-md-5">
                                        <b-form-select
                                            v-model="sub_settings[7].bonding_agent_firm_req"
                                            size="sm"
                                            class="mt-1"
                                            :options="[{text: 'Required', value: 1}, {text: 'Optional', value: 0}]" />
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <label class="col-md-4 text-md-right control-label text-muted">Bonding Phone:</label>
                                    <div class="col-md-5">
                                        <b-form-select
                                            v-model="sub_settings[7].bonding_phone_req"
                                            size="sm"
                                            class="mt-1"
                                            :options="[{text: 'Required', value: 1}, {text: 'Optional', value: 0}]" />
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <label class="col-md-4 text-md-right control-label text-muted">Max. Overall Bonding Capacity:</label>
                                    <div class="col-md-5">
                                        <b-form-select
                                            v-model="sub_settings[7].bonding_max_capacity_req"
                                            size="sm"
                                            class="mt-1"
                                            :options="[{text: 'Required', value: 1}, {text: 'Optional', value: 0}]" />
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <label class="col-md-4 text-md-right control-label text-muted">Max. Bonding Capacity Per Contract:</label>
                                    <div class="col-md-5">
                                        <b-form-select
                                            v-model="sub_settings[7].bonding_max_contract_req"
                                            size="sm"
                                            class="mt-1"
                                            :options="[{text: 'Required', value: 1}, {text: 'Optional', value: 0}]" />
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <label class="col-md-4 text-md-right control-label text-muted">Bonding Company Start Date:</label>
                                    <div class="col-md-5">
                                        <b-form-select
                                            v-model="sub_settings[7].bonding_start_date_req"
                                            size="sm"
                                            class="mt-1"
                                            :options="[{text: 'Required', value: 1}, {text: 'Optional', value: 0}]" />
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <label class="col-md-4 text-md-right control-label text-muted">Previous Bonding Companies:</label>
                                    <div class="col-md-5">
                                        <b-form-select
                                            v-model="sub_settings[7].bonding_previous_companies_req"
                                            size="sm"
                                            class="mt-1"
                                            :options="[{text: 'Required', value: 1}, {text: 'Optional', value: 0}]" />
                                    </div>
                                </div>
                                <hr/>

                                <custom-field-edit
                                    ref="customFieldEdit_Section_7"
                                    :sectionId="7"
                                    :enableAddToList="true"
                                    :startingFields="(custom_field_sections[7] && custom_field_sections[7].fields ? custom_field_sections[7].fields : [])"
                                    @fieldsChanged="((fields) => { this.custom_field_sections[7].fields = fields; })" />
                            </div>
                        </div>
                    </div>
                    <div class="card" style="margin-bottom: 50px">
                        <div class="card-header">
                            <strong>Section 8: Past Performance</strong>

                            <div class="float-right">
                                <button class="btn btn-sm btn-light" @click="addField(8)" title="Add Custom Field">
                                    <i class="fa fa-plus-circle text-success"></i> Custom Field
                                </button>
                            </div>
                        </div>
                        <div class="card-body">
                            <div v-if="loading" class="text-center">
                                <i class="fa fa-circle-o-notch fa-spin"></i> Loading
                            </div>
                            <div v-else>
                                <div>
                                    -- No Settings --
                                </div>
                                <hr/>

                                <custom-field-edit
                                    ref="customFieldEdit_Section_8"
                                    :sectionId="8"
                                    :enableAddToList="true"
                                    :startingFields="(custom_field_sections[8] && custom_field_sections[8].fields ? custom_field_sections[8].fields : [])"
                                    @fieldsChanged="((fields) => { this.custom_field_sections[8].fields = fields; })" />
                            </div>
                        </div>
                    </div>
                    <div class="card" style="margin-bottom: 50px">
                        <div class="card-header">
                            <strong>Section 9: References</strong>

                            <div class="float-right">
                                <button class="btn btn-sm btn-light" @click="addField(9)" title="Add Custom Field">
                                    <i class="fa fa-plus-circle text-success"></i> Custom Field
                                </button>
                            </div>
                        </div>
                        <div class="card-body">
                            <div v-if="loading" class="text-center">
                                <i class="fa fa-circle-o-notch fa-spin"></i> Loading
                            </div>
                            <div v-else>
                                <div class="form-group row">
                                    <label class="col-md-4 text-md-right control-label text-muted">Number of Trade/Credit References Required:</label>
                                    <div class="col-md-5">
                                        <b-form-input v-model="sub_settings[9].num_trade_credit_refs" type="number" min="0" step="1" size="sm" placeholder="Enter Number of Trade/Credit References" />
                                        <div class="text-muted small">
                                            Enter "0" if you would like to make this field optional.
                                        </div>
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <label class="col-md-4 text-md-right control-label text-muted">Number of Project References Required:</label>
                                    <div class="col-md-5">
                                        <b-form-input v-model="sub_settings[9].num_project_refs" type="number" min="0" step="1" size="sm" placeholder="Enter Number of Project References" />
                                        <div class="text-muted small">
                                            Enter "0" if you would like to make this field optional.
                                        </div>
                                    </div>
                                </div>
                                <hr/>

                                <custom-field-edit
                                    ref="customFieldEdit_Section_9"
                                    :sectionId="9"
                                    :enableAddToList="true"
                                    :startingFields="(custom_field_sections[9] && custom_field_sections[9].fields ? custom_field_sections[9].fields : [])"
                                    @fieldsChanged="((fields) => { this.custom_field_sections[9].fields = fields; })" />
                            </div>
                        </div>
                    </div>
                    <div class="card" style="margin-bottom: 50px">
                        <div class="card-header">
                            <strong>Section 10: EEO & ELIGIBILITY TO WORK</strong>

                            <div class="float-right">
                                <button class="btn btn-sm btn-light" @click="addField(10)" title="Add Custom Field">
                                    <i class="fa fa-plus-circle text-success"></i> Custom Field
                                </button>
                            </div>
                        </div>
                        <div class="card-body">
                            <div v-if="loading" class="text-center">
                                <i class="fa fa-circle-o-notch fa-spin"></i> Loading
                            </div>
                            <div v-else>
                                <div>
                                    -- No Settings --
                                </div>
                                <hr/>

                                <custom-field-edit
                                    ref="customFieldEdit_Section_10"
                                    :sectionId="10"
                                    :enableAddToList="true"
                                    :startingFields="(custom_field_sections[10] && custom_field_sections[10].fields ? custom_field_sections[10].fields : [])"
                                    @fieldsChanged="((fields) => { this.custom_field_sections[10].fields = fields; })" />
                            </div>
                        </div>
                    </div>

                    <button type="button" class="btn btn-sm btn-success" @click="saveSettings()" :disabled="saving">
                        Update Pre-Qualification Form Settings
                    </button>
                </div>

                <div v-else-if="curTab === 'alerts'">
                    <div class="card mb-4">
                        <div class="card-header">
                            <strong>Custom Email Alerts</strong>
                        </div>
                        <div class="card-body">
                            <email-alert-list :assetType="'SubPreQual'"></email-alert-list>
                        </div>
                    </div>

                    <div class="card">
                        <div class="card-header">
                            <strong>System Email Alert Recipients</strong>
                        </div>
                        <div class="card-body">
                            <div class="alert alert-info">
                                <p>There are three system email alerts that are triggered by various events. By default, each of these will include ALL active users who are assigned the "Subcontractors - Manage" permission.</p>
                                <ol>
                                    <li>
                                        <strong>Subcontractor Edited</strong>
                                        <ul>
                                            <li>Triggered when a Non-Admin/Manager User Edits a Pre-Qualification Profile while Status = In Progress or Renewal in Progress.</li>
                                            <li>Triggered on ALL sections, but you can add custom recipients below for individual sections</li>
                                        </ul>
                                    </li>
                                    <li>
                                        <strong>Subcontractor Status Changed</strong>
                                        <ul>
                                            <li>Triggered when the overall Pre-Qualification status changes.</li>
                                            <li>This excludes individual section status changes.</li>
                                        </ul>
                                    </li>
                                    <li>
                                        <strong>Comments on Pre-Qualification</strong>
                                        <ul>
                                            <li>Triggered when any comment is made on the Pre-Qualification.</li>
                                            <li>This includes overall comment and section comments.</li>
                                        </ul>
                                    </li>
                                </ol>

                                <p>Default Recipients Based on the "Subcontractor - Manage" Permission:</p>
                                <ol>
                                    <li v-for="recip in sub_alert_recipients">{{ recip.FirstName }} {{ recip.LastName }} - {{ recip.email }}</li>
                                </ol>
                            </div>
                            <hr/>

                            <table class="table table-bordered">
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th>Triggers</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(user, i) in sub_alert_users">
                                        <td>
                                            <user-select
                                                :selectedValues="user.user_id ? {UserId: user.user_id, FirstName: user.FirstName, LastName: user.LastName} : null"
                                                @vuselected="function(data){ user.user_id = data.UserId; }"
                                                :hasError="errors['recipients['+i+']'] ? true : false" />

                                            <div class="mt-2">
                                                <a href="javascript:void(0)" class="text-danger" v-on:click="deleteSubAlertUser(i)">
                                                    <i class="fa fa-trash"></i> Delete
                                                </a>
                                            </div>
                                        </td>
                                        <td>
                                            <b-form-checkbox-group v-model="user.alert_triggers" stacked plain>
                                                <template v-for="(trigger, i) in alertTriggerOptions">
                                                    <b-form-checkbox :value="trigger.value">{{ trigger.text }}</b-form-checkbox>
                                                    <b-form-checkbox-group
                                                        v-model="user.alert_trigger_options['SubcontractorEdited']"
                                                        :options="alertSectionOptions"
                                                        class="ml-4" stacked plain
                                                        v-if="trigger.value === 'SubcontractorEdited' && user.alert_triggers && user.alert_triggers.indexOf(trigger.value) != -1" />

                                                    <b-form-checkbox-group
                                                        v-model="user.alert_trigger_options['CommentNotification']"
                                                        :options="alertSectionOptions"
                                                        class="ml-4" stacked plain
                                                        v-if="trigger.value === 'CommentNotification' && user.alert_triggers && user.alert_triggers.indexOf(trigger.value) != -1" />

                                                    <b-form-checkbox-group
                                                        v-model="user.alert_trigger_options['SubcontractorStatusEmail']"
                                                        :options="alertStatusOptions"
                                                        class="ml-4"stacked plain
                                                        v-if="trigger.value === 'SubcontractorStatusEmail' && user.alert_triggers && user.alert_triggers.indexOf(trigger.value) != -1" />
                                                </template>
                                            </b-form-checkbox-group>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <div id="new-sub-alert-user" class="text-center" v-on:click="addAlertUser()">
                                <span class="text-success"><i class="fa fa-plus-circle"></i> Add Alert Recipient</span>
                            </div>
                        </div>
                        <div class="card-footer">
                            <button type="button" class="btn btn-sm btn-success" v-on:click="saveAlertSettings()" :disabled="saving_alerts">
                                Save Alert Settings <i class="fa fa-spin fa-spinner" v-if="saving_alerts"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!--
        <div class="card mt-3">
            <div class="card-header">
                <h4 class="mb-0">OSHA API Settings</h4>
            </div>
            <div class="card-body">
                <div v-if="loading" class="text-center">
                    <i class="fa fa-circle-o-notch fa-spin"></i> Loading
                </div>
                <div v-else>
                    <div class="form-group row">
                        <label class="col-md-3 col-sm-4 col-form-label text-sm-right">
                            OSHA API Token:
                        </label>
                        <div class="col-sm-8">
                            <b-form-textarea v-model="account.osha_key" rows="4"></b-form-textarea>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        -->

        <b-modal v-model="show_fields" title="Add Field Preview" size="lg" no-fade :no-close-on-backdrop="true" ok-variant="success" ok-title="Add Field" @ok="fieldAdded">

            <b-form-radio-group plain v-model="new_field">
                <strong>Standard Fields</strong>
                <ul class="list-group mb-4 bg-white">
                    <li class="list-group-item" v-for="field in fields" :key="field.id">
                        <b-form-radio :value="field.id">{{ field.type }}</b-form-radio>
                    </li>
                </ul>

                <strong>Formatted Fields</strong>
                <ul class="list-group mb-4 bg-white">
                    <li class="list-group-item" v-for="field in formattedFields" :key="field.id">
                      <b-form-radio :value="field.id">{{ field.type }}</b-form-radio>
                    </li>
                </ul>

                <strong>Lookup Fields - These are pulled from your lists of data.</strong>
                <ul class="list-group mb-4 bg-white">
                    <li class="list-group-item" v-for="field in lookUpFields" :key="field.id">
                      <b-form-radio :value="field.id">{{ field.type }}</b-form-radio>
                    </li>
                </ul>

                <strong>Helpers - Insert Help Messages or Reminders for Users filling out your form.</strong>
                <ul class="list-group bg-white">
                    <li class="list-group-item" v-for="field in helperFields" :key="field.id">
                      <b-form-radio :value="field.id">{{ field.type }}</b-form-radio>
                    </li>
                </ul>
            </b-form-radio-group>
        </b-modal>
    </div>
</template>

<script>
    export default {
        data: function() {
            return {
                saving: false,
                saving_alerts: false,
                loading: true,
                show_fields: false,
                curTab: 'general',
                account: {},
                errors: [],
                sub_alert_recipients: [],
                sub_alert_users: [],
                alert_triggers: {},
                alert_sections: {},

                new_field: null,
                editing_section: null,

                //Setup
                custom_field_sections: {
                    1: {
                        id: '',
                        show_body: 1,
                        order: 1,
                        base_section: 1,
                        section_name: null,
                        fields: [],
                    },
                    2: {
                        id: '',
                        show_body: 1,
                        order: 1,
                        base_section: 1,
                        section_name: null,
                        fields: [],
                    },
                    3: {
                        id: '',
                        show_body: 1,
                        order: 1,
                        base_section: 1,
                        section_name: null,
                        fields: [],
                    },
                    4: {
                        id: '',
                        show_body: 1,
                        order: 1,
                        base_section: 1,
                        section_name: null,
                        fields: [],
                    },
                    5: {
                        id: '',
                        show_body: 1,
                        order: 1,
                        base_section: 1,
                        section_name: null,
                        fields: [],
                    },
                    6: {
                        id: '',
                        show_body: 1,
                        order: 1,
                        base_section: 1,
                        section_name: null,
                        fields: [],
                    },
                    7: {
                        id: '',
                        show_body: 1,
                        order: 1,
                        base_section: 1,
                        section_name: null,
                        fields: [],
                    },
                    8: {
                        id: '',
                        show_body: 1,
                        order: 1,
                        base_section: 1,
                        section_name: null,
                        fields: [],
                    },
                    9: {
                        id: '',
                        show_body: 1,
                        order: 1,
                        base_section: 1,
                        section_name: null,
                        fields: [],
                    },
                    10: {
                        id: '',
                        show_body: 1,
                        order: 1,
                        base_section: 1,
                        section_name: null,
                        fields: [],
                    },
                },

                sub_settings: {},

                settingsTabs: {
                    general: 'General Settings',
                    prequalification: 'Pre-Qualification Form',
                    field_trainings: 'Field Training List',
                    trades: 'Trade List',
                    alerts: 'Email Alert Settings',
                },

                fields: [
                    {id: 1, type: 'Text', placeholder: '', parent_field_id: null},
                    {id: 2, type: 'Dropdown', placeholder: '', parent_field_id: null, options: {}, allow_multiple: false},
                    {id: 3, type: 'Radio', parent_field_id: null, options: {}, display_vertical: false, enable_other: false},
                    {id: 4, type: 'Checkbox', parent_field_id: null, options: {}, display_vertical: false, enable_other: false, allow_multiple: false},
                    {id: 5, type: 'Textarea', parent_field_id: null, placeholder: '', enable_rich_text: false},
                    {id: 18, type: 'File', parent_field_id: null, placeholder: ''},
                ],
                formattedFields: [
                    {id: 6, type: 'Date', parent_field_id: null, placeholder: '', format: 'MM/DD/YYYY'},
                    {id: 7, type: 'Time', parent_field_id: null, placeholder: ''},
                    {id: 8, type: 'Email', parent_field_id: null, placeholder: ''},
                    {id: 9, type: 'Phone', parent_field_id: null, placeholder: '', format: 'XXX-XXX-XXXX'},
                    {id: 10, type: 'Number', parent_field_id: null, placeholder: '', min: null, max: null, step: null},
                ],
                helperFields: [
                    {id: 12, type: 'Heading', parent_field_id: null, tag: 'h1'},
                    {id: 13, type: 'Paragraph', parent_field_id: null, color: 'info', text: ''},
                ],
                lookUpFields: [
                    {id: 14, type: 'Employee', placeholder: '', parent_field_id: null, options: {}, allow_multiple: false},
                    {id: 15, type: 'Equipment', placeholder: '', parent_field_id: null, options: {}, allow_multiple: false},
                    {id: 16, type: 'Jobsite', placeholder: '', parent_field_id: null, options: {}, allow_multiple: false},
                    {id: 17, type: 'User', placeholder: '', parent_field_id: null, options: {}, allow_multiple: false},
                ],
            }
        },

        watch: {
            curTab(newValue, oldValue) {
                if (newValue)
                {
                    if (newValue === 'general')
                    {
                        window.history.replaceState(null,null,`${this.$route.path}`)
                    }
                    else
                    {
                        window.history.replaceState(null,null,`${this.$route.path}#${this.curTab}`)
                    }
                }
            },
        },

        computed: {
            tabButtonTitle(){
                //Send it
                return '<i class="fa fa-bars"></i> ' + this.settingsTabs[this.curTab];
            },

            alertTriggerOptions(){
                //Setup
                var options = [];

                //SEtup keys
                var keys = Object.keys(this.alert_triggers);

                //Build it
                for (var i = 0; i < keys.length; i++)
                {
                    //Push it
                    options.push({text: this.alert_triggers[keys[i]].label, value: keys[i]});
                }

                //Send it
                return options;
            },

            alertSectionOptions(){
                //Setup
                var options = [];

                //SEtup keys
                var keys = Object.keys(this.alert_triggers['SubcontractorEdited'].options);

                //Build it
                for (var i = 0; i < keys.length; i++)
                {
                    //Push it
                    options.push({text: (keys[i] + '. ' + this.alert_triggers['SubcontractorEdited'].options[keys[i]]), value: keys[i]});
                }

                //Send it
                return options;
            },

            alertStatusOptions(){
                //Setup
                var options = [];

                //SEtup keys
                var keys = Object.keys(this.alert_triggers['SubcontractorStatusEmail'].options);

                //Build it
                for (var i = 0; i < keys.length; i++)
                {
                    //Push it
                    options.push({text: (keys[i] + '. ' + this.alert_triggers['SubcontractorStatusEmail'].options[keys[i]]), value: keys[i]});
                }

                //Send it
                return options;
            },
        },

        methods: {
            getSettings() {
                //Get the data
                this.loading = true;
                this.$http
                    .get('subcontractors/settings')
                    .then(response => {
                        //Set all the data
                        this.account = response.data.account;
                        this.sub_settings = response.data.default_sub_settings;
                        this.sub_alert_recipients = response.data.sub_alert_recipients;
                        this.sub_alert_users = response.data.sub_alert_users;
                        this.alert_triggers = response.data.alert_triggers;

                        //Check for Setting
                        if(response.data.sub_settings)
                        {
                            //Merge SEtting
                            this.mergeSettings(response.data.sub_settings);
                        }

                        //Check for sections
                        for (var i = 0; i < response.data.sections.length; i++)
                        {
                            //Check the Section
                            this.custom_field_sections[parseInt(response.data.sections[i].base_section)] = response.data.sections[i];
                        }

                        //Set loading
                        this.loading = false;
                    })
                    .catch(response => {

                    });
            },

            newQuestion(){
                this.account.sub_rating_questions.push({question: '', max_rating: ''});
            },

            deleteQuestion(index){
                //Slice Out
                this.account.sub_rating_questions.splice(index, 1);
            },

            putSettingsInStore(user) {
                user.sub_years_required = this.account.sub_years_required;
                user.sub_expire_date = this.account.sub_expire_date;

                return user;
            },

            saveSettings(reset = false) {
                //SHow loading
                this.saving = true;

                //Reset the Errors
                this.errors = [];

                //Loop and Set the Errors
                var keys = Object.keys(this.custom_field_sections);

                //Set ALL the error PER section
                for (var i = 0; i < keys.length; i++)
                {
                    //Check for undefined
                    if(typeof this.$refs['customFieldEdit_Section_'+keys[i]] != 'undefined')
                    {
                        //Update the errors for the Custom Field Builder
                        this.$refs['customFieldEdit_Section_'+keys[i]].setErrors(this.errors);
                    }
                }

                //Append the Custom FIelds
                this.account.custom_field_sections = this.custom_field_sections;

                //Set the Settings
                this.account.sub_settings = this.sub_settings;

                //Set it
                this.$http.post('subcontractors/settings', this.account)
                    .then(response => {
                        //Alert the user?
                        this.$toastr.success('Settings Saved');

                        this.errors = [];

                        let user = this.$store.state.user;
                        user = this.putSettingsInStore(user);
                        this.$store.commit('login', JSON.parse(JSON.stringify(user)));

                        // We no longed have unsaved colors
                        this.unsaved_colors = false;
                    })
                    .catch(error => {
                        //Check for error
                        if (error.response.status === 422)
                        {
                            //Display validation errors
                            this.errors = error.response.data;

                            //Loop and Set the Errors
                            var keys = Object.keys(this.custom_field_sections);

                            //Set ALL the error PER section
                            for (var i = 0; i < keys.length; i++)
                            {
                                //Check for undefined
                                if(typeof this.$refs['customFieldEdit_Section_'+keys[i]] != 'undefined')
                                {
                                    //Update the errors for the Custom Field Builder
                                    this.$refs['customFieldEdit_Section_'+keys[i]].setErrors(this.errors);
                                }
                            }
                        }
                    })
                    .then(response => {
                        //SHow loading
                        this.saving = false;

                        //Delete the Include Files
                        delete this.account.includes_files;
                    });
            },

            addField(key){
                //Setter
                this.show_fields = true;

                //Adding to section
                this.editing_section = key;
            },

            fieldAdded(bvEvt) {
                //Make sure the user selected a field
                if(!this.new_field)
                {
                    //Alert the user
                    this.$toastr.warning('Select a Field to Add');

                    //Keep the modal open
                    bvEvt.preventDefault();

                    //Stop here
                    return;
                }

                //Get the field by ID and send it
                var fieldDefinition = this.getFieldById(this.new_field);


                //Instantiate if not set
                if(!this.custom_field_sections[this.editing_section])
                {
                    //Setup
                    this.custom_field_sections[this.editing_section] = {
                        settings: {},
                        fields: [],
                    };
                }

                //Clone it
                this.custom_field_sections[this.editing_section].fields.push(this.cloneField(fieldDefinition, this.custom_field_sections[this.editing_section].fields.length));

                //Reset
                this.editing_section = null;
                this.new_field = null;
            },

            getFieldById(id){
                //Loop through all
                for(var i = 0; i < this.fields.length; i++)
                {
                    //Check the if
                    if(id == this.fields[i].id)
                    {
                        return this.fields[i];
                    }
                }

                //Loop through all
                for(var i = 0; i < this.formattedFields.length; i++)
                {
                    //Check the if
                    if(id == this.formattedFields[i].id)
                    {
                        return this.formattedFields[i];
                    }
                }
                //Loop through all
                for(var i = 0; i < this.lookUpFields.length; i++)
                {
                    //Check the if
                    if(id == this.lookUpFields[i].id)
                    {
                        return this.lookUpFields[i];
                    }
                }

                //Loop through all
                for(var i = 0; i < this.helperFields.length; i++)
                {
                    //Check the if
                    if(id == this.helperFields[i].id)
                    {
                        return this.helperFields[i];
                    }
                }
            },

            //Clones a Field to a New Element
            cloneField(field, numExistingFields){
                //Setup
                var newField = JSON.parse(JSON.stringify(field));

                //Modify the field
                newField.id = field.type.toLowerCase() + '-' + new Date().getTime();
                newField.show_body = true;
                newField.core_field_key = null;
                newField.core_field_value = null;
                newField.order = numExistingFields;

                //Send it
                return newField;
            },

            mergeSettings(database_settings){
                //Check for Array and Empty
                if(Array.isArray(database_settings) && database_settings.length === 0)
                {
                    return;
                }

                //Get the Keys and Loop
                var keys = Object.keys(database_settings);

                //Loop and Process
                for (var i = 0; i < keys.length; i++)
                {
                    //Add the Section to the Base
                    if( !(keys[i] in this.sub_settings) )
                    {
                        //Create it
                        this.sub_settings[keys[i]] = {};
                    }

                    //Process the SEttings
                    var field_keys = Object.keys(database_settings[keys[i]]);

                    //Loop
                    for (var j = 0; j < field_keys.length; j++)
                    {
                        //Loop and Add EACH key to the section
                        // if( !(field_keys[j] in this.sub_settings[keys[i]]) )
                        // {
                        //     //Create it
                        //     this.sub_settings[keys[i]][field_keys[j]] = null;
                        // }

                        //Set the value
                        this.sub_settings[keys[i]][field_keys[j]] = database_settings[keys[i]][field_keys[j]];
                    }
                }
            },

            addAlertUser(){
                //Setup the User
                let user = {
                    user_id: null,
                    alert_triggers: [],
                    alert_trigger_options: {},
                };

                //Push Each of the Empty Triggers onto the Options
                var keys = Object.keys(this.alert_triggers);

                //Push each
                for (var i = 0; i < keys.length; i++)
                {
                    user.alert_trigger_options[keys[i]] = [];
                }

                //Push it
                this.sub_alert_users.push(user);
            },

            deleteSubAlertUser(i){
                //Splice it
                this.sub_alert_users.splice(i, 1);
            },

            saveAlertSettings(){
                //Setup
                this.saving_alerts = true;
                this.errors = [];

                //Format All the Users
                for (var i = 0; i < this.sub_alert_users.length; i++)
                {
                    //Setup the Alert Settings
                    this.sub_alert_users[i].alert_settings = {};

                    //Loop through and Add the Triggers
                    for (var j = 0; j < this.sub_alert_users[i].alert_triggers.length; j++)
                    {
                        //Setup
                        this.sub_alert_users[i].alert_settings[this.sub_alert_users[i].alert_triggers[j]] = [];

                        //Check if set
                        if(this.sub_alert_users[i].alert_trigger_options[this.sub_alert_users[i].alert_triggers[j]])
                        {
                            this.sub_alert_users[i].alert_settings[this.sub_alert_users[i].alert_triggers[j]] = this.sub_alert_users[i].alert_trigger_options[this.sub_alert_users[i].alert_triggers[j]]
                        }
                    }
                }

                //Send it
                this.$http.post('/subcontractors/settings/alerts', this.sub_alert_users)
                    .then(response => {
                        //Alert the user?
                        this.$toastr.success('System Alert Settings Saved');

                        //Reset the Errors
                        this.errors = [];
                    })
                    .catch(error => {
                        //Check for error
                        if (error.response.status === 422)
                        {
                            //Display validation errors
                            this.errors = error.response.data;
                        }
                    })
                    .then(response => {
                        //SHow loading
                        this.saving_alerts = false;
                    });
            },
        },

        mounted() {
            this.getSettings();

            if (this.$route.hash !== '' && this.$route.hash !== '#')
            {
                this.curTab = this.$route.hash.replace('#','')
            }
        },
    }
</script>

<style type="text/css" scoped>
    #new-sub-alert-user {
        padding: 5px 8px;
        border: 1px dashed #ccc;
    }

    #new-sub-alert-user:hover {
        cursor: pointer;
    }
</style>